// =============================================================================
// BP-07-03: CategoryNav Component
// Category sidebar navigation with nested categories
// =============================================================================

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  Book,
  Scale,
  Heart,
  Shield,
  GraduationCap,
  Stethoscope,
  Home,
  Users,
  Loader2,
} from 'lucide-react';
import { useResourceCategories, useCategoryTree } from '@/features/resources/hooks/useResourceCategories';
import type { ResourceCategory } from '@/features/resources/types/resourcesTypes';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface CategoryNavProps {
  variant?: 'sidebar' | 'horizontal' | 'dropdown';
  showCount?: boolean;
  selectedSlug?: string;
  onSelect?: (category: ResourceCategory) => void;
  className?: string;
}

// Icon mapping for categories
const categoryIcons: Record<string, React.ElementType> = {
  medical: Stethoscope,
  legal: Scale,
  insurance: Shield,
  education: GraduationCap,
  therapy: Heart,
  benefits: Home,
  community: Users,
  default: Book,
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function CategoryNav({
  variant = 'sidebar',
  showCount = true,
  selectedSlug,
  onSelect,
  className,
}: CategoryNavProps) {
  const location = useLocation();
  const { data: categories, isLoading, error } = useResourceCategories();
  const categoryTree = useCategoryTree();
  
  // Track expanded categories
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Toggle category expansion
  const toggleExpand = (categoryId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // Get icon for category
  const getCategoryIcon = (slug: string) => {
    return categoryIcons[slug] || categoryIcons.default;
  };

  // Check if category is active
  const isActive = (category: ResourceCategory) => {
    if (selectedSlug) {
      return category.slug === selectedSlug;
    }
    return location.pathname === `/resources/category/${category.slug}`;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center py-8', className)}>
        <Loader2 className="w-6 h-6 text-sage animate-spin" />
      </div>
    );
  }

  // Error state
  if (error || !categories) {
    return (
      <div className={cn('text-center py-8 text-warm-gray', className)}>
        <p>Unable to load categories</p>
      </div>
    );
  }

  // Horizontal variant
  if (variant === 'horizontal') {
    return (
      <nav className={cn('flex items-center gap-2 overflow-x-auto pb-2', className)}>
        <Link
          to="/resources"
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-colors',
            !selectedSlug
              ? 'bg-sage text-white'
              : 'bg-cream hover:bg-cream-dark text-navy'
          )}
        >
          <Book className="w-4 h-4" />
          <span className="text-sm font-medium">All Resources</span>
        </Link>
        
        {categories.filter(c => !c.parentId).map((category) => {
          const Icon = getCategoryIcon(category.slug);
          const active = isActive(category);
          
          return (
            <Link
              key={category.id}
              to={`/resources/category/${category.slug}`}
              onClick={() => onSelect?.(category)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-colors',
                active
                  ? 'bg-sage text-white'
                  : 'bg-cream hover:bg-cream-dark text-navy'
              )}
            >
              <Icon className="w-4 h-4" />
              <span className="text-sm font-medium">{category.name}</span>
              {showCount && category.resourceCount !== undefined && (
                <span className={cn(
                  'text-xs px-1.5 py-0.5 rounded-full',
                  active ? 'bg-white/20' : 'bg-warm-gray/10'
                )}>
                  {category.resourceCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
    );
  }

  // Dropdown variant
  if (variant === 'dropdown') {
    return (
      <div className={cn('relative', className)}>
        <CategoryDropdown
          categories={categories}
          selectedSlug={selectedSlug}
          showCount={showCount}
          onSelect={onSelect}
        />
      </div>
    );
  }

  // Sidebar variant (default)
  return (
    <nav className={cn('space-y-1', className)} aria-label="Resource categories">
      {/* All Resources link */}
      <Link
        to="/resources"
        className={cn(
          'flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors',
          !selectedSlug && location.pathname === '/resources'
            ? 'bg-sage text-white'
            : 'text-navy hover:bg-cream'
        )}
      >
        <Book className="w-5 h-5" />
        <span className="font-medium">All Resources</span>
      </Link>

      {/* Category tree */}
      {categoryTree.map((category) => (
        <CategoryNavItem
          key={category.id}
          category={category}
          level={0}
          isActive={isActive}
          expandedIds={expandedIds}
          toggleExpand={toggleExpand}
          getCategoryIcon={getCategoryIcon}
          showCount={showCount}
          onSelect={onSelect}
        />
      ))}
    </nav>
  );
}

// -----------------------------------------------------------------------------
// CategoryNavItem (recursive for nested categories)
// -----------------------------------------------------------------------------

interface CategoryNavItemProps {
  category: ResourceCategory & { children?: ResourceCategory[] };
  level: number;
  isActive: (category: ResourceCategory) => boolean;
  expandedIds: Set<string>;
  toggleExpand: (id: string) => void;
  getCategoryIcon: (slug: string) => React.ElementType;
  showCount: boolean;
  onSelect?: (category: ResourceCategory) => void;
}

function CategoryNavItem({
  category,
  level,
  isActive,
  expandedIds,
  toggleExpand,
  getCategoryIcon,
  showCount,
  onSelect,
}: CategoryNavItemProps) {
  const hasChildren = category.children && category.children.length > 0;
  const isExpanded = expandedIds.has(category.id);
  const active = isActive(category);
  const Icon = getCategoryIcon(category.slug);

  return (
    <div>
      <div className="flex items-center">
        {/* Expand button for items with children */}
        {hasChildren && (
          <button
            type="button"
            onClick={() => toggleExpand(category.id)}
            className="p-1.5 hover:bg-cream rounded transition-colors"
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${category.name}`}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-warm-gray" />
            ) : (
              <ChevronRight className="w-4 h-4 text-warm-gray" />
            )}
          </button>
        )}

        {/* Category link */}
        <Link
          to={`/resources/category/${category.slug}`}
          onClick={() => onSelect?.(category)}
          className={cn(
            'flex-1 flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors',
            !hasChildren && 'ml-7',
            active
              ? 'bg-sage text-white'
              : 'text-navy hover:bg-cream'
          )}
          style={{ paddingLeft: hasChildren ? undefined : `${level * 12 + 16}px` }}
        >
          <Icon className={cn('w-5 h-5', active ? 'text-white' : 'text-warm-gray')} />
          <span className="flex-1 font-medium">{category.name}</span>
          {showCount && category.resourceCount !== undefined && (
            <span className={cn(
              'text-xs px-2 py-0.5 rounded-full',
              active ? 'bg-white/20' : 'bg-cream text-warm-gray'
            )}>
              {category.resourceCount}
            </span>
          )}
        </Link>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="mt-1 ml-2 pl-4 border-l-2 border-cream-dark">
          {category.children!.map((child) => (
            <CategoryNavItem
              key={child.id}
              category={child as ResourceCategory & { children?: ResourceCategory[] }}
              level={level + 1}
              isActive={isActive}
              expandedIds={expandedIds}
              toggleExpand={toggleExpand}
              getCategoryIcon={getCategoryIcon}
              showCount={showCount}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// CategoryDropdown
// -----------------------------------------------------------------------------

interface CategoryDropdownProps {
  categories: ResourceCategory[];
  selectedSlug?: string;
  showCount?: boolean;
  onSelect?: (category: ResourceCategory) => void;
}

function CategoryDropdown({
  categories,
  selectedSlug,
  showCount,
  onSelect,
}: CategoryDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedCategory = categories.find((c) => c.slug === selectedSlug);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-full flex items-center justify-between gap-2 px-4 py-3 bg-white border border-warm-gray/20 rounded-lg transition-colors',
          isOpen && 'border-sage ring-2 ring-sage/20'
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="flex items-center gap-2">
          {selectedCategory ? (
            <>
              {(() => {
                const Icon = categoryIcons[selectedCategory.slug] || Book;
                return <Icon className="w-5 h-5 text-sage" />;
              })()}
              <span className="font-medium text-navy">{selectedCategory.name}</span>
            </>
          ) : (
            <>
              <Book className="w-5 h-5 text-warm-gray" />
              <span className="text-warm-gray">All Categories</span>
            </>
          )}
        </div>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-warm-gray transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <ul
            className="absolute z-50 w-full mt-2 bg-white border border-warm-gray/20 rounded-lg shadow-lifted max-h-64 overflow-y-auto"
            role="listbox"
          >
            <li>
              <Link
                to="/resources"
                onClick={() => {
                  setIsOpen(false);
                  onSelect?.(undefined as any);
                }}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 transition-colors',
                  !selectedSlug
                    ? 'bg-sage/10 text-sage'
                    : 'hover:bg-cream text-navy'
                )}
                role="option"
                aria-selected={!selectedSlug}
              >
                <Book className="w-5 h-5" />
                <span className="font-medium">All Categories</span>
              </Link>
            </li>
            {categories.filter(c => !c.parentId).map((category) => {
              const Icon = categoryIcons[category.slug] || Book;
              const isSelected = category.slug === selectedSlug;
              
              return (
                <li key={category.id}>
                  <Link
                    to={`/resources/category/${category.slug}`}
                    onClick={() => {
                      setIsOpen(false);
                      onSelect?.(category);
                    }}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 transition-colors',
                      isSelected
                        ? 'bg-sage/10 text-sage'
                        : 'hover:bg-cream text-navy'
                    )}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="flex-1 font-medium">{category.name}</span>
                    {showCount && category.resourceCount !== undefined && (
                      <span className="text-xs text-warm-gray">
                        {category.resourceCount}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

export default CategoryNav;

